import React from 'react';
import EmbedSettings from './EmbedSettings';

/**
 * DeploymentDetail component - Detailed view for a selected deployment
 * @param {Object} props - Component props
 * @param {Object} props.selectedDeployment - The selected deployment
 * @param {string} props.activeProjectId - Current active project ID
 * @param {Array} props.linkedFamilies - Linked families
 * @param {Array} props.releases - Available releases
 * @param {string} props.detailTab - Current detail tab ('embed' | 'builder' | 'analytics')
 * @param {Function} props.setDetailTab - Setter for detail tab
 * @param {Function} props.onBack - Handler for back button
 * @param {Function} props.onToggleActive - Handler for toggle active
 * @param {Function} props.onSaveSettings - Handler for save settings
 * @param {Function} props.onOpenDesigner - Handler for opening the selected deployment in the designer route
 * @param {boolean} props.isActive - Current active state
 * @param {boolean} props.isSubmitting - Whether form is submitting
 * @param {string} props.error - Error message
 * @param {string} props.successMsg - Success message
 * @param {string} props.editName - Edit name value
 * @param {Function} props.setEditName - Setter for edit name
 * @param {string} props.editGuiVersionTag - Edit GUI version tag
 * @param {Function} props.setEditGuiVersionTag - Setter for edit GUI version tag
 * @param {string} props.allowedOrigins - Allowed origins value
 * @param {Function} props.setAllowedOrigins - Setter for allowed origins
 * @param {string} props.editReleaseId - Edit release ID
 * @param {Function} props.setEditReleaseId - Setter for edit release ID
 * @param {string} props.expiresAt - Expiration date
 * @param {Function} props.setExpiresAt - Setter for expiration date
 * @param {boolean} props.copiedToken - Whether token was copied
 * @param {Function} props.setCopiedToken - Setter for copied token state
 * @param {boolean} props.copiedSnippet - Whether snippet was copied
 * @param {Function} props.setCopiedSnippet - Setter for copied snippet state
 * @param {boolean} props.copiedWidget - Whether widget was copied
 * @param {Function} props.setCopiedWidget - Setter for copied widget state
 * @param {string} props.activeEmbedTab - Current embed tab ('iframe' | 'widget')
 * @param {Function} props.setActiveEmbedTab - Setter for embed tab
 */
const DeploymentDetail = ({
    selectedDeployment,
    activeProjectId,
    linkedFamilies,
    releases,
    detailTab,
    setDetailTab,
    onBack,
    onToggleActive,
    onSaveSettings,
    onOpenDesigner,
    isActive,
    isSubmitting,
    error,
    successMsg,
    editName,
    setEditName,
    editGuiVersionTag,
    setEditGuiVersionTag,
    allowedOrigins,
    setAllowedOrigins,
    editReleaseId,
    setEditReleaseId,
    expiresAt,
    setExpiresAt,
    copiedToken,
    setCopiedToken,
    copiedSnippet,
    setCopiedSnippet,
    copiedWidget,
    setCopiedWidget,
    activeEmbedTab,
    setActiveEmbedTab
}) => {
    if (!selectedDeployment) return null;

    return (
        <>
            <header style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', flexShrink: 0 }}>
                <BackButton onClick={onBack} />
                
                <HeaderContent
                    selectedDeployment={selectedDeployment}
                    isActive={isActive}
                    onOpenDesigner={onOpenDesigner}
                />
            </header>

            <TabNavigation detailTab={detailTab} setDetailTab={setDetailTab} />

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {detailTab === 'embed' && (
                    <EmbedSettings
                        selectedDeployment={selectedDeployment}
                        projectId={activeProjectId}
                        releases={releases}
                        isActive={isActive}
                        isSubmitting={isSubmitting}
                        error={error}
                        successMsg={successMsg}
                        editName={editName}
                        setEditName={setEditName}
                        editGuiVersionTag={editGuiVersionTag}
                        setEditGuiVersionTag={setEditGuiVersionTag}
                        allowedOrigins={allowedOrigins}
                        setAllowedOrigins={setAllowedOrigins}
                        editReleaseId={editReleaseId}
                        setEditReleaseId={setEditReleaseId}
                        expiresAt={expiresAt}
                        setExpiresAt={setExpiresAt}
                        onToggleActive={onToggleActive}
                        onSaveSettings={onSaveSettings}
                        copiedToken={copiedToken}
                        setCopiedToken={setCopiedToken}
                        copiedSnippet={copiedSnippet}
                        setCopiedSnippet={setCopiedSnippet}
                        copiedWidget={copiedWidget}
                        setCopiedWidget={setCopiedWidget}
                        activeEmbedTab={activeEmbedTab}
                        setActiveEmbedTab={setActiveEmbedTab}
                    />
                )}

                {detailTab === 'analytics' && (
                    <AnalyticsView selectedDeployment={selectedDeployment} isActive={isActive} />
                )}
            </div>
        </>
    );
};

/**
 * Back button component
 */
const BackButton = ({ onClick }) => (
    <button
        onClick={onClick}
        style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--text-secondary)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.35rem',
            padding: 0,
            fontSize: '0.85rem',
            width: 'fit-content'
        }}
        onMouseEnter={e => e.currentTarget.style.color = 'var(--text-primary)'}
        onMouseLeave={e => e.currentTarget.style.color = 'var(--text-secondary)'}
    >
        <i className="fa-solid fa-arrow-left"></i> Back to Deployments Overview
    </button>
);

/**
 * Header content showing deployment info
 */
const HeaderContent = ({ selectedDeployment, isActive, onOpenDesigner }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
            <h1 style={{ fontSize: '1.35rem', margin: 0, color: 'var(--text-primary)' }}>
                {selectedDeployment.name}
            </h1>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: '0.2rem 0 0 0', display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span>
                    <i className="fa-solid fa-box" style={{ marginRight: '0.3rem', color: 'var(--accent)' }}></i>
                    Family: <strong>{selectedDeployment.productFamilyName}</strong>
                </span>
                <span>
                    <i className="fa-solid fa-palette" style={{ marginRight: '0.3rem' }}></i>
                    GUI Layout Tag: <strong>{selectedDeployment.guiVersionTag}</strong>
                </span>
            </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
                type="button"
                className="btn-primary"
                onClick={onOpenDesigner}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 0.9rem', fontSize: '0.85rem' }}
            >
                <i className="fa-solid fa-compass-drafting"></i>
                Open in GUI Designer
            </button>
            <StatusBadge isActive={isActive} />
        </div>
    </div>
);

/**
 * Status badge for deployment header
 */
const StatusBadge = ({ isActive }) => (
    <span style={{
        padding: '0.35rem 0.75rem',
        borderRadius: '12px',
        fontSize: '0.8rem',
        fontWeight: 600,
        background: isActive ? 'rgba(5, 150, 105, 0.1)' : 'rgba(220, 38, 38, 0.1)',
        color: isActive ? 'var(--success)' : 'var(--error)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.3rem'
    }}>
        <i className={`fa-solid ${isActive ? 'fa-circle-check' : 'fa-circle-xmark'}`}></i>
        {isActive ? 'Active Token' : 'Disabled'}
    </span>
);

/**
 * Tab navigation component
 */
const TabNavigation = ({ detailTab, setDetailTab }) => (
    <div style={{ display: 'flex', gap: '1rem', borderBottom: '1px solid var(--border)', paddingBottom: '0.5rem', flexShrink: 0 }}>
        <TabButton
            active={detailTab === 'embed'}
            onClick={() => setDetailTab('embed')}
            icon="fa-code"
            label="Integration & Security Settings"
        />
        <TabButton
            active={detailTab === 'analytics'}
            onClick={() => setDetailTab('analytics')}
            icon="fa-chart-line"
            label="Deployment Analytics"
        />
    </div>
);

/**
 * Individual tab button
 */
const TabButton = ({ active, onClick, icon, label }) => (
    <button
        type="button"
        onClick={onClick}
        style={{
            background: 'transparent',
            border: 'none',
            borderBottom: active ? '2px solid var(--text-primary)' : '2px solid transparent',
            color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
            fontWeight: active ? 600 : 400,
            padding: '0.5rem 1rem',
            cursor: 'pointer',
            fontSize: '0.9rem',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.4rem'
        }}
    >
        <i className={`fa-solid ${icon}`}></i> {label}
    </button>
);

/**
 * Analytics view component
 */
const AnalyticsView = ({ selectedDeployment, isActive }) => (
    <div className="fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <MetricsGrid selectedDeployment={selectedDeployment} />
        
        <SessionChart deploymentName={selectedDeployment.name} />
    </div>
);

/**
 * Metrics grid for analytics
 */
const MetricsGrid = ({ selectedDeployment }) => (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.25rem' }}>
        <MetricCard
            label="Embed Loads (30d)"
            value="1,482"
            trend="+8.2% vs last month"
            trendType="positive"
        />
        <MetricCard
            label="Completion Rate"
            value="98.4%"
            valueColor="var(--success)"
            description="Configurator funnel active"
        />
        <MetricCard
            label="Avg. Session Time"
            value="2.4 min"
            description="Interactive session health"
            trendType="positive"
        />
        <MetricCard
            label="Domain Restriction"
            value="Active"
            valueColor="var(--accent)"
            description="Lock to Whitelist Domains"
        />
    </div>
);

/**
 * Individual metric card
 */
const MetricCard = ({ label, value, valueColor = 'var(--text-primary)', description, trend, trendType }) => (
    <div style={{ 
        background: 'var(--bg-card)', 
        padding: '1rem', 
        border: '1px solid var(--border)', 
        borderRadius: 'var(--radius)', 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '0.25rem' 
    }}>
        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', textTransform: 'uppercase' }}>
            {label}
        </span>
        <span style={{ fontSize: '1.4rem', fontWeight: 700, color: valueColor }}>
            {value}
        </span>
        {trend ? (
            <span style={{ fontSize: '0.7rem', color: 'var(--success)', fontWeight: 600 }}>
                <i className="fa-solid fa-arrow-trend-up"></i> {trend}
            </span>
        ) : (
            <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
                {description}
            </span>
        )}
    </div>
);

/**
 * Session chart placeholder
 */
const SessionChart = ({ deploymentName }) => (
    <div style={{ 
        background: 'var(--bg-card)', 
        border: '1px solid var(--border)', 
        borderRadius: 'var(--radius)', 
        padding: '1rem', 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '1rem' 
    }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', margin: 0 }}>
            Daily Session Analytics for {deploymentName}
        </h3>
        
        <div style={{ 
            height: '220px', 
            border: '1px dashed var(--border)', 
            borderRadius: '8px', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            background: 'var(--bg-secondary)', 
            flexDirection: 'column', 
            gap: '0.5rem' 
        }}>
            <i className="fa-solid fa-chart-area" style={{ fontSize: '2.5rem', color: 'var(--accent)', opacity: 0.7 }}></i>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
                Live Telemetry Standby
            </span>
            <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', opacity: 0.6 }}>
                Interactive charts will initialize upon live traffic registration on this deployment token.
            </span>
        </div>
    </div>
);

export default DeploymentDetail;
export { 
    BackButton, 
    HeaderContent, 
    StatusBadge, 
    TabNavigation, 
    TabButton,
    AnalyticsView,
    MetricsGrid,
    MetricCard,
    SessionChart
};
